import { useState, useEffect } from 'react';
import api from '../api/client';
import { useTranslation } from 'react-i18next';

export default function Temporada() {
  const { t } = useTranslation();

  const [resumen, setResumen] = useState(null);
  const [produccion, setProduccion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fallos, setFallos] = useState(0);

  // Las dos peticiones son independientes: si una falla, la otra se sigue
  // mostrando. Solo se considera error de pantalla cuando fallan las dos.
  useEffect(() => {
    let cancelado = false;
    Promise.allSettled([
      api.get('/resumen/temporada'),
      api.get('/resumen/produccion'),
    ]).then(([rTemporada, rProduccion]) => {
      if (cancelado) return;
      if (rTemporada.status === 'fulfilled') setResumen(rTemporada.value.data);
      if (rProduccion.status === 'fulfilled') setProduccion(rProduccion.value.data);
      setFallos([rTemporada, rProduccion].filter(r => r.status === 'rejected').length);
      setLoading(false);
    });
    return () => { cancelado = true; };
  }, []);

  const hayProduccion = produccion && produccion.anios.length > 0;
  const error = fallos === 2;

  return (
    <div>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>{t('temporada.title')}</h1>
          <p style={S.sub}>{t('temporada.subtitle')}</p>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#8B6355' }}>{t('common.loading')}</div>
      ) : error ? (
        <div className="empty-state">
          <div className="icon">⚠️</div>
          <h3>{t('temporada.errorTitle')}</h3>
          <p>{t('temporada.errorMsg')}</p>
        </div>
      ) : (
        <>
          {resumen && (
            <div style={S.resumenCard}>
              <div style={S.resumenTitle}>{t('temporada.season', { year: resumen.año })}</div>
              <div style={S.resumenGrid}>
                <ResumenItem icon="🐝" label={t('temporada.activeHives')} value={resumen.colmenas_activas} />
                <ResumenItem icon="📓" label={t('temporada.totalEvents')} value={resumen.total_eventos} />
                <ResumenItem icon="✓" label={t('temporada.completedTasks')} value={resumen.tareas_completadas} />
                <ResumenItem icon="🫙" label={t('temporada.harvests')} value={resumen.eventos_por_tipo?.cosecha || 0} />
                {resumen.cosecha_total_kg != null && (
                  <ResumenItem icon="🍯" label={t('temporada.harvestKg')} value={`${resumen.cosecha_total_kg} kg`} />
                )}
                <ResumenItem icon="💊" label={t('temporada.treatments')} value={resumen.eventos_por_tipo?.tratamiento || 0} />
                <ResumenItem icon="🔍" label={t('temporada.inspections')} value={resumen.eventos_por_tipo?.inspeccion || 0} />
              </div>
            </div>
          )}

          <div style={S.resumenCard}>
            <div style={S.resumenTitle}>🍯 {t('temporada.prodTitle')}</div>
            {!hayProduccion ? (
              <div style={S.prodEmpty}>{t('temporada.prodEmpty')}</div>
            ) : (
              <>
              <div style={{ overflowX: 'auto' }}>
                <table style={S.prodTable}>
                  <thead>
                    <tr>
                      <th style={{ ...S.prodTh, textAlign: 'left' }}>{t('temporada.prodHive')}</th>
                      {produccion.anios.map(a => (
                        <th key={a} style={S.prodTh}>{a}</th>
                      ))}
                      {produccion.anios.length > 1 && <th style={S.prodTh}>{t('temporada.prodDiff')}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {produccion.colmenas.map(c => {
                      const ultimo = c.por_anio[String(produccion.anios[0])];
                      const previo = c.por_anio[String(produccion.anios[1])];
                      const diff = ultimo && previo ? ultimo.kg - previo.kg : null;
                      return (
                        <tr key={c.colmena_id}>
                          <td style={{ ...S.prodTd, textAlign: 'left', fontWeight: 500 }}>
                            {c.nombre}{!c.activa && <span style={{ color: '#C0A090', fontWeight: 400 }}> · {t('temporada.prodInactive')}</span>}
                          </td>
                          {produccion.anios.map(a => {
                            const d = c.por_anio[String(a)];
                            if (!d) return <td key={a} style={{ ...S.prodTd, color: '#C0A090' }}>{t('temporada.prodNoData')}</td>;
                            return (
                              <td key={a} style={S.prodTd}>
                                {d.kg} kg
                                {d.ceros_con_motivo > 0 && (
                                  <span title={t('temporada.prodZeroReasonTip')} style={S.prodZeroBadge}>
                                    ⓪ {t('temporada.prodZeroReason', { count: d.ceros_con_motivo })}
                                  </span>
                                )}
                              </td>
                            );
                          })}
                          {produccion.anios.length > 1 && (
                            <td style={{ ...S.prodTd, color: diff == null ? '#C0A090' : diff >= 0 ? '#1E8449' : '#C0392B', fontWeight: 600 }}>
                              {diff == null ? '—' : `${diff >= 0 ? '+' : ''}${Math.round(diff * 100) / 100} kg`}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td style={{ ...S.prodTd, textAlign: 'left', fontWeight: 700, borderTop: '2px solid #F0EDE9' }}>{t('temporada.prodTotal')}</td>
                      {produccion.anios.map(a => {
                        const tot = produccion.totales[String(a)];
                        return (
                          <td key={a} style={{ ...S.prodTd, fontWeight: 700, borderTop: '2px solid #F0EDE9' }}>
                            {tot ? `${tot.kg} kg` : '—'}
                          </td>
                        );
                      })}
                      {produccion.anios.length > 1 && (() => {
                        const t0 = produccion.totales[String(produccion.anios[0])];
                        const t1 = produccion.totales[String(produccion.anios[1])];
                        const d = t0 && t1 ? t0.kg - t1.kg : null;
                        return (
                          <td style={{ ...S.prodTd, fontWeight: 700, borderTop: '2px solid #F0EDE9', color: d == null ? '#C0A090' : d >= 0 ? '#1E8449' : '#C0392B' }}>
                            {d == null ? '—' : `${d >= 0 ? '+' : ''}${Math.round(d * 100) / 100} kg`}
                          </td>
                        );
                      })()}
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div style={{ fontSize: 11, color: '#8B6355', marginTop: 8 }}>{t('temporada.prodLegend')}</div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ResumenItem({ icon, label, value }) {
  return (
    <div style={{ textAlign: 'center', padding: '10px 8px' }}>
      <div style={{ fontSize: 22 }}>{icon}</div>
      <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "'DM Sans', sans-serif" }}>{value}</div>
      <div style={{ fontSize: 11, color: '#8B6355' }}>{label}</div>
    </div>
  );
}

const S = {
  header: { marginBottom: 20 },
  title: { fontSize: 26, fontFamily: "'DM Sans', sans-serif" },
  sub: { color: '#8B6355', fontSize: 14, marginTop: 4 },
  resumenCard: { background: 'white', borderRadius: 16, padding: '16px 20px', marginBottom: 24, boxShadow: '0 2px 12px rgba(44,26,14,0.08)', border: '1px solid rgba(232,146,42,0.12)' },
  resumenTitle: { fontSize: 14, fontWeight: 600, color: '#8B6355', marginBottom: 12, fontFamily: "'DM Sans', sans-serif" },
  resumenGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 8 },
  prodTable: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  prodTh: { padding: '6px 10px', textAlign: 'right', fontSize: 11, fontWeight: 600, color: '#8B6355', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #F0EDE9', whiteSpace: 'nowrap' },
  prodTd: { padding: '7px 10px', textAlign: 'right', borderBottom: '1px solid #F8F4EF', whiteSpace: 'nowrap' },
  prodEmpty: { fontSize: 13, color: '#8B6355', padding: '6px 0 2px' },
  prodZeroBadge: { display: 'inline-block', marginLeft: 6, padding: '1px 7px', borderRadius: 20, fontSize: 10, fontWeight: 500, background: '#FDF6E3', color: '#7D6608' },
};
